import { GpxPoint } from './types';

const R = 6371e3; // Earth's radius in meters

function toRad(degrees: number): number {
  return degrees * Math.PI / 180;
}

/**
 * Calculates the distance between two points using the Haversine formula.
 */
function haversineDistance(p1: GpxPoint, p2: GpxPoint): number {
  const dLat = toRad(p2.lat - p1.lat);
  const dLon = toRad(p2.lon - p1.lon);
  const lat1 = toRad(p1.lat);
  const lat2 = toRad(p2.lat);

  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  
  return R * c; // Distance in meters
}

/**
 * Interpolates a point along a line segment between two points.
 * Uses linear interpolation for lat/lon and optionally elevation/time.
 */
function interpolatePoint(p1: GpxPoint, p2: GpxPoint, fraction: number): GpxPoint {
  const lat = p1.lat + (p2.lat - p1.lat) * fraction;
  const lon = p1.lon + (p2.lon - p1.lon) * fraction;
  
  let ele: number | undefined = undefined;
  if (p1.ele !== undefined && p2.ele !== undefined) {
    ele = p1.ele + (p2.ele - p1.ele) * fraction;
  } else if (p1.ele !== undefined) {
      ele = p1.ele; // Carry over if only start has elevation
  } else if (p2.ele !== undefined) {
      ele = p2.ele; // Use end elevation if only end has it (less likely needed but for completeness)
  }

  // Time interpolation (optional, requires parsing time strings)
  // For simplicity, we'll omit time interpolation for now. 
  // It would require converting time strings to timestamps, interpolating, and converting back.
  
  return { lat, lon, ele }; 
}


/**
 * Resamples a GPX track to have points at a specified distance interval.
 * 
 * @param points - The original array of GpxPoint objects.
 * @param intervalMeters - The desired distance between points in meters.
 * @returns A new array of GpxPoint objects representing the resampled track.
 */
export function resampleByDistance(points: GpxPoint[], intervalMeters: number): GpxPoint[] {
  if (points.length < 2 || intervalMeters <= 0) {
    return [...points]; // Return a copy if input is too short or interval is invalid
  }

  const resampledPoints: GpxPoint[] = [points[0]]; // Always include the first point
  let cumulativeDistance = 0;
  let distanceToNextInterval = intervalMeters;

  for (let i = 1; i < points.length; i++) {
    const p1 = points[i - 1];
    const p2 = points[i];
    const segmentDistance = haversineDistance(p1, p2);

    if (segmentDistance === 0) continue; // Skip duplicate points

    let distanceAlongSegment = 0;

    while (cumulativeDistance + segmentDistance >= distanceToNextInterval) {
        const remainingDistanceToInterval = distanceToNextInterval - cumulativeDistance;
        const fraction = remainingDistanceToInterval / segmentDistance;
        
        const interpolated = interpolatePoint(p1, p2, fraction);
        resampledPoints.push(interpolated);

        distanceAlongSegment += remainingDistanceToInterval;
        cumulativeDistance += remainingDistanceToInterval;
        distanceToNextInterval += intervalMeters;
    }

    // Update cumulative distance with the remainder of the segment
    cumulativeDistance += (segmentDistance - distanceAlongSegment);
  }

  // Optionally add the last point if it wasn't added by interpolation
  // This ensures the full track extent is covered.
  const lastOriginal = points[points.length - 1];
  const lastResampled = resampledPoints[resampledPoints.length - 1];
  if (lastResampled.lat !== lastOriginal.lat || lastResampled.lon !== lastOriginal.lon) {
       // Calculate distance from last resampled point to the original last point
       const finalSegmentDistance = haversineDistance(lastResampled, lastOriginal);
       // Add the last point only if it's significantly far from the last resampled point,
       // avoids adding points very close together at the end.
       if (finalSegmentDistance > intervalMeters / 10) { // e.g., more than 10% of the interval away
           resampledPoints.push(lastOriginal);
       }
  }


  return resampledPoints;
} 